<template>
  <content-modal
    class="message-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :primary-button-disabled="props.params.primaryButtonDisabled"
    :show-close="props.params.showClose"
    :auto-hide-off="autoHideOff"
    @modal-shown="actionShown(props.params.actionShown)"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="props.params.use_label" #label>{{ props.params.label }}</template>
    <template v-if="props.params.use_title" #title>{{ props.params.title }}</template>
    <template #content>
      <modal-content>
        <template v-if="props.params.use_content" #content>
          <div v-safe-html="props.params.content" class="info" />
          <template v-if="hasSubContent">
            <div class="description">
              <h5 class="title">
                <svg-icon name="Info32" kind="indicative" />
                <span>{{ t("take_note_title") }}</span>
              </h5>
            </div>
            <cv-list>
              <cv-list-item v-for="(item, index) in props.params.subContent" :key="index">
                <h6 class="title">{{ item.title }}</h6>
                <span class="text">{{ item.text }}</span>
              </cv-list-item>
            </cv-list>
          </template>
        </template>
        <template #tools>
          <cv-button
            v-if="props.params.use_secondaryButton"
            kind="secondary"
            :disabled="dataButtonState.loading"
            @click.stop="actionSecondary(props.params.actionSecondary)"
            >{{ props.params.secondaryBtnLabel }}</cv-button
          >
          <template v-if="props.params.use_primaryButton">
            <fetch-data-button
              v-if="props.params.useStateButton"
              :kind="props.params.primaryBtnKind"
              :loading="dataButtonState.loading"
              :completed="dataButtonState.completed"
              :error="dataButtonState.error"
              :error-message="dataButtonState.errorMessage"
              :tip-position="dataButtonState.tipPosition"
              @click.stop="actionPrimary(props.params.actionPrimary)"
              @reset-error-state="handleResetErrorState"
              @reset-completed-state="handleResetCompletedState"
            >
              <span v-if="dataButtonState.completed">{{
                props.params.primaryBtnCompletedLabel
              }}</span>
              <span v-else-if="dataButtonState.error">{{ props.params.primaryBtnErrorLabel }}</span>
              <span v-else>{{ props.params.primaryBtnLabel }}</span>
            </fetch-data-button>
            <cv-button
              v-else
              :kind="props.params.primaryBtnKind"
              @click.stop="actionPrimary(props.params.actionPrimary)"
              >{{ props.params.primaryBtnLabel }}</cv-button
            >
          </template>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import { DATA_STATE, DataState } from "@plugins/modal/modal.constant";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useVm } from "@utils/vm.ts";

interface SubContentItem {
  title: string;
  text: string;
}

interface MessageModalParams {
  primaryButtonDisabled?: boolean;
  showClose?: boolean;
  use_label?: boolean;
  label?: string;
  use_title?: boolean;
  title?: string;
  use_content?: boolean;
  content?: string;
  subContent?: SubContentItem[];
  use_secondaryButton?: boolean;
  secondaryBtnLabel?: string;
  actionSecondary?: () => void;
  use_primaryButton?: boolean;
  primaryBtnKind?: "primary" | "danger";
  primaryBtnLabel?: string;
  primaryBtnCompletedLabel?: string;
  primaryBtnErrorLabel?: string;
  actionPrimary?: (ctx?: unknown) => void;
  actionShown?: () => void;
  actionHidden?: () => void;
  actionHideRequest?: () => void;
  modelClosed?: () => void;
  useStateButton?: boolean;
  autoHideOff?: boolean;
  actionPostSuccess?: () => void;
}

const props = withDefaults(defineProps<{ params?: MessageModalParams }>(), {
  params: () => ({}),
});

const { t } = useI18n();
const { vmNode } = useVm();

const closeAriaLabel = ref("Close");
const size = ref("small");
const autoHideOff = ref(props.params.autoHideOff ?? true);
const visible = ref(true);

const dataButtonState = reactive({
  tipPosition: "bottom",
  loading: false,
  completed: false,
  error: false,
  errorMessage: t("api_action_generic_error_message"),
});

const defaultErrorMessage = ref(t("api_action_generic_error_message"));

const hasSubContent = computed(
  () => Array.isArray(props.params.subContent) && props.params.subContent.length > 0,
);

onUnmounted(() => {
  if (
    typeof props.params.modelClosed !== "undefined" &&
    typeof props.params.modelClosed === "function"
  ) {
    props.params.modelClosed();
  }
});

function actionShown(actionEvent?: () => void) {
  if (typeof actionEvent !== "undefined" && typeof actionEvent === "function") {
    actionEvent();
  }
}

function actionHidden() {
  if (
    typeof props.params.actionHidden !== "undefined" &&
    typeof props.params.actionHidden === "function"
  ) {
    props.params.actionHidden();
  }
  modalEventBus.$emit("modal-hidden", vmNode.key);
  visible.value = false;
}

function actionPrimary(actionEvent?: (ctx?: unknown) => void) {
  if (typeof actionEvent !== "undefined" && typeof actionEvent === "function") {
    if (props.params.useStateButton) {
      if (getDataState() !== DATA_STATE.DEFAULT) {
        return;
      }
      actionEvent({
        close,
        getDataState,
        setDataState,
        setErrorMessage,
        handleResetErrorState,
        handleResetCompletedState,
        resetDataState,
        dataButtonState,
      });
      return;
    } else {
      actionEvent();
    }
  }
  visible.value = false;
}

function actionSecondary(actionEvent?: () => void) {
  if (dataButtonState.loading) {
    return;
  }
  if (typeof actionEvent !== "undefined" && typeof actionEvent === "function") {
    actionEvent();
  }
  visible.value = false;
}

function actionHideRequest(event: Record<string, unknown>) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    const actionEvent = props.params.actionHideRequest;
    if (typeof actionEvent !== "undefined" && typeof actionEvent === "function") {
      actionEvent();
    }
    modalEventBus.$emit("modal-hidden", vmNode.key);
    visible.value = false;
  }
}

function close() {
  visible.value = false;
}

function getDataState(): DataState | undefined {
  if (props.params.useStateButton) {
    if (dataButtonState.loading) {
      return DATA_STATE.LOADING;
    } else if (dataButtonState.completed) {
      return DATA_STATE.COMPLETED;
    } else if (dataButtonState.error) {
      return DATA_STATE.ERROR;
    } else {
      return DATA_STATE.DEFAULT;
    }
  }
  return undefined;
}

function setDataState(state: DataState) {
  if (props.params.useStateButton) {
    resetDataState();
    switch (state) {
      case DATA_STATE.LOADING:
        dataButtonState.loading = true;
        return;
      case DATA_STATE.COMPLETED:
        dataButtonState.completed = true;
        return;
      case DATA_STATE.ERROR:
        dataButtonState.error = true;
        return;
    }
  }
}

function setErrorMessage(message: string) {
  dataButtonState.errorMessage = message;
}

function handleResetErrorState() {
  if (props.params.useStateButton) {
    dataButtonState.error = false;
    dataButtonState.errorMessage = defaultErrorMessage.value;
  }
}

function handleResetCompletedState() {
  if (!props.params.useStateButton) return;

  // set completed state to be false
  dataButtonState.completed = false;

  // trigger any post success action if given.
  if (props.params.actionPostSuccess && typeof props.params.actionPostSuccess === "function") {
    props.params.actionPostSuccess();
  }
}

function resetDataState() {
  if (props.params.useStateButton) {
    dataButtonState.loading = false;
    dataButtonState.completed = false;
    dataButtonState.error = false;
    dataButtonState.errorMessage = defaultErrorMessage.value;
  }
}

defineExpose({
  close,
  getDataState,
  setDataState,
  setErrorMessage,
  handleResetErrorState,
  handleResetCompletedState,
  resetDataState,
});
</script>
